/**
 * The FHIR resource types the document-extraction-v2 pipeline projects, which
 * are exactly the types a clinician can approve or reject.
 *
 * This is the single source of truth for every review surface (Zori Pulse, the
 * extraction modal, the extraction task audit, provenance feedback). Hand-kept
 * copies of this list drifted apart before: adding `MedicationRequest` in one
 * place left the extraction task audit silently skipping persisted medication
 * orders while every other type hydrated with approve/reject controls. Derive
 * unions, sets, and service maps from this list instead of restating it.
 *
 * `analytics.ts` is the one deliberate exception: its `ReviewableResourceType`
 * restates these as literals so the analytics privacy invariant can prove the
 * event payload is bounded. Adding a type here without adding it there breaks
 * the build at the review surfaces' `track` calls.
 */
export const REVIEWABLE_RESOURCE_TYPE_LIST = [
  'AllergyIntolerance',
  'Condition',
  'Immunization',
  'MedicationRequest',
  'MedicationStatement',
  'Observation',
  'Procedure',
] as const;

export type ReviewableResourceType =
  (typeof REVIEWABLE_RESOURCE_TYPE_LIST)[number];

/**
 * Typed as `ReadonlySet<string>` because callers test arbitrary FHIR resource
 * type strings (task output, clicked rows) for membership. Use
 * `isReviewableResourceType` when the result needs to narrow.
 */
export const REVIEWABLE_RESOURCE_TYPES: ReadonlySet<string> = new Set(
  REVIEWABLE_RESOURCE_TYPE_LIST,
);

export const isReviewableResourceType = (
  resourceType: string | null | undefined,
): resourceType is ReviewableResourceType =>
  !!resourceType && REVIEWABLE_RESOURCE_TYPES.has(resourceType);
